import React, {useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import {Search as SearchIcon} from "@mui/icons-material";
import {
    Calendar,
    Filter,
    Lock,
    MoreVertical,
    Plus,
    Shield,
    ShieldAlert,
    Trash2,
    Unlock,
    UserCheck,
    UserCog,
    UserX,
    Users
} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {FeedbackSnackbar, PageHeader} from "../../shared/ui";
import {useUsers} from "./useUsers";
import {useAcessoAUsuarios} from "./useAcessoAUsuarios";
import {getAccessTypeColor, USER_ACCESS_TYPE_OPTIONS} from "./usersUtils";
import UserDialog from "./components/UserDialog";
import PortalNaLista from "./components/PortalNaLista";

const userMenus = (navigate) => ([
    {
        label: 'Gestão de Usuários',
        id: 'users',
        selected: true,
        disabled: false,
        icon: <Users size={18} />,
        onClick: () => navigate('/usuarios'),
    },
]);

const statCards = (stats) => ([
    {label: 'Total de Usuários', value: stats.total, icon: UserCheck, color: '#0f766e', bg: '#ccfbf1'},
    {label: 'Ativos', value: stats.active, icon: UserCheck, color: '#16a34a', bg: '#dcfce7'},
    {label: 'Bloqueados', value: stats.blocked, icon: Lock, color: '#dc2626', bg: '#fee2e2'},
    {label: 'Admins', value: stats.admins, icon: Shield, color: '#0f766e', bg: '#ccfbf1'},
    {label: 'Suporte', value: stats.support, icon: ShieldAlert, color: '#2563eb', bg: '#dbeafe'},
    {label: 'Renovação Pendente', value: stats.renewPassword, icon: Calendar, color: '#d97706', bg: '#fef3c7'},
]);

export default function UsersPage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(openParent);
    const [quickFilter, setQuickFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuUser, setMenuUser] = useState(null);
    const acesso = useAcessoAUsuarios();
    const data = useUsers({enabled: acesso.pode});

    const rows = useMemo(() => data.rows.filter((row) => {
        if (quickFilter === 'all') return true;
        if (quickFilter === 'admins') return row.specialAccessType === 'ADMIN';
        if (quickFilter === 'support') return row.specialAccessType === 'SUPPORT';
        if (quickFilter === 'user') return row.specialAccessType === 'USER';
        if (quickFilter === 'blocked') return row.blocked;
        if (quickFilter === 'renew_password') return row.mustRenewPassword;
        return true;
    }), [data.rows, quickFilter]);

    const paginatedRows = useMemo(() => (
        rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    ), [page, rows, rowsPerPage]);

    const handleMenuOpen = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setMenuUser(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuUser(null);
    };

    const clearFilters = () => {
        data.setFilter('');
        data.setAccessTypeFilter('');
        data.setStatusFilter('');
        data.setBlockedFilter('');
        setQuickFilter('all');
        setPage(0);
    };

    const header = (
        <PageHeader
            title="Gestão de Usuários"
            titleAction={() => navigate("/")}
            itemsMenu={userMenus(navigate)}
            onHandleMenuOpen={() => null}
            onHandleDrawerToggle={setOpen}
            openParent={open}
        />
    );
    const mainSx = {
        flexGrow: 1,
        p: {xs: 2.5, md: 4},
        minWidth: 0,
        boxSizing: 'border-box',
        transition: theme.transitions.create(['margin', 'width']),
        mt: 8,
        backgroundColor: '#f9fafb',
        minHeight: 'calc(100dvh - 64px)',
    };

    if (!acesso.pode) {
        return (
            <Box sx={{display: 'flex', minHeight: '100dvh'}}>
                {header}
                <Box component="main" sx={mainSx}>
                    {acesso.carregando ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : (
                        <Alert severity="warning">
                            Você não tem permissão para ver a gestão de usuários. Se necessário, solicite acesso ao administrador.
                        </Alert>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            {header}
            <Box component="main" sx={mainSx}>
                <Stack spacing={3}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap'}}>
                        <Box>
                            <Typography variant="h4" fontWeight={700} gutterBottom>
                                Gestão de Usuários
                            </Typography>
                            <Typography color="text.secondary">
                                Crie, atualize e acompanhe a equipe Busifarm (ADMIN e SUPPORT) e os usuários de todas as assinaturas (USER), com os vínculos de cada uma.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<Plus size={18} />} onClick={data.openCreate}>
                            Novo usuário
                        </Button>
                    </Box>

                    <FeedbackSnackbar
                        feedback={data.feedback}
                        onClose={() => data.setFeedback({error: '', success: ''})}
                    />

                    <Grid container spacing={2}>
                        {statCards(data.stats).map((item) => {
                            const Icon = item.icon;
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={2} key={item.label}>
                                    <Card variant="outlined" sx={{height: '100%', borderRadius: 3}}>
                                        <CardContent>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: item.bg,
                                                        color: item.color,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Icon size={20} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="h5" fontWeight={700}>{item.value}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Box>
                        <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 1}}>
                            {[
                                {value: 'all', label: 'Todos'},
                                {value: 'admins', label: 'Admins'},
                                {value: 'support', label: 'Suporte'},
                                {value: 'user', label: 'Usuários'},
                                {value: 'blocked', label: 'Bloqueados'},
                                {value: 'renew_password', label: 'Renovação Pendente'},
                            ].map((item) => (
                                <Chip
                                    key={item.value}
                                    label={item.label}
                                    onClick={() => {
                                        setQuickFilter(item.value);
                                        setPage(0);
                                    }}
                                    color={quickFilter === item.value ? 'primary' : 'default'}
                                    variant={quickFilter === item.value ? 'filled' : 'outlined'}
                                    sx={quickFilter === item.value ? {bgcolor: '#0f766e'} : undefined}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Paper sx={{p: 3, borderRadius: 3}} variant="outlined">
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Buscar por nome ou e-mail"
                                    value={data.filter}
                                    onChange={(event) => {
                                        data.setFilter(event.target.value);
                                        setPage(0);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{opacity: 0.65}} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Tipo de Acesso"
                                    value={data.accessTypeFilter}
                                    onChange={(event) => {
                                        data.setAccessTypeFilter(event.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {USER_ACCESS_TYPE_OPTIONS.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Status"
                                    value={data.statusFilter}
                                    onChange={(event) => {
                                        data.setStatusFilter(event.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="ATIVO">Ativo</MenuItem>
                                    <MenuItem value="INATIVO">Inativo</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Bloqueio"
                                    value={data.blockedFilter}
                                    onChange={(event) => {
                                        data.setBlockedFilter(event.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="SIM">Bloqueado</MenuItem>
                                    <MenuItem value="NAO">Não bloqueado</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button startIcon={<Filter size={16} />} onClick={clearFilters}>
                                    Limpar filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper variant="outlined" sx={{overflow: 'hidden', borderRadius: 3}}>
                        <Box sx={{overflowX: 'auto'}}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Usuário</TableCell>
                                        <TableCell>E-mail</TableCell>
                                        <TableCell>Tipo de Acesso</TableCell>
                                        <TableCell>Portal</TableCell>
                                        <TableCell>Licenças</TableCell>
                                        <TableCell>Roles</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Bloqueio</TableCell>
                                        <TableCell>Expiração</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.loading ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{py: 8}}>
                                                <CircularProgress size={20} />
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                    {!data.loading && !paginatedRows.length ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{py: 8}}>
                                                <Typography color="text.secondary">Nenhum usuário encontrado</Typography>
                                                <Button size="small" sx={{mt: 2}} onClick={clearFilters}>
                                                    Limpar filtros
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                    {paginatedRows.map((row) => (
                                        <TableRow key={row.userId || row.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {row.username || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {(row.userId || row.id || '').slice(0, 8)}...
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{row.email || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.specialAccessType || '-'}
                                                    color={getAccessTypeColor(row.specialAccessType)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <PortalNaLista conta={row} />
                                            </TableCell>
                                            <TableCell>
                                                {row.licenses?.length > 0 ? (
                                                    <Chip label={String(row.licenses.length)} size="small" variant="outlined" />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Sem licença</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {row.roles?.length > 0 ? (
                                                    <Chip label={String(row.roles.length)} size="small" variant="outlined" />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Sem role</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.enabled ? 'Ativo' : 'Inativo'}
                                                    color={row.enabled ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.blocked ? 'Bloqueado' : 'Não bloqueado'}
                                                    color={row.blocked ? 'error' : 'default'}
                                                    size="small"
                                                    variant={row.blocked ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {row.expirationDate ? row.expirationDateFmt : 'Não expira'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={(event) => handleMenuOpen(event, row)}>
                                                    <MoreVertical size={18} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(Number(event.target.value));
                                setPage(0);
                            }}
                            labelRowsPerPage="Linhas por página:"
                            labelDisplayedRows={({from, to, count}) => `${from}-${to} de ${count}`}
                        />
                    </Paper>
                </Stack>
            </Box>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
                <MenuItem onClick={() => {
                    if (menuUser) data.openEdit(menuUser);
                    handleMenuClose();
                }}>
                    <UserCog size={16} />
                    <Box sx={{ml: 1}}>Visualizar/Editar</Box>
                </MenuItem>
                <MenuItem onClick={() => {
                    if (menuUser) data.updateStatus(menuUser, {enabled: !menuUser.enabled});
                    handleMenuClose();
                }}>
                    {menuUser?.enabled ? <UserX size={16} /> : <UserCheck size={16} />}
                    <Box sx={{ml: 1}}>{menuUser?.enabled ? 'Desativar' : 'Ativar'}</Box>
                </MenuItem>
                <MenuItem onClick={() => {
                    if (menuUser) data.updateStatus(menuUser, {blocked: !menuUser.blocked});
                    handleMenuClose();
                }}>
                    {menuUser?.blocked ? <Unlock size={16} /> : <Lock size={16} />}
                    <Box sx={{ml: 1}}>{menuUser?.blocked ? 'Desbloquear' : 'Bloquear'}</Box>
                </MenuItem>
                <MenuItem onClick={() => {
                    if (menuUser) data.updateStatus(menuUser, {mustRenewPassword: !menuUser.mustRenewPassword});
                    handleMenuClose();
                }}>
                    <Shield size={16} />
                    <Box sx={{ml: 1}}>{menuUser?.mustRenewPassword ? 'Remover renovação' : 'Exigir renovação'}</Box>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        data.setConfirmDelete(menuUser);
                        handleMenuClose();
                    }}
                    sx={{color: 'error.main'}}
                >
                    <Trash2 size={16} />
                    <Box sx={{ml: 1}}>Excluir</Box>
                </MenuItem>
            </Menu>

            <UserDialog
                open={data.dialogOpen}
                onClose={() => data.setDialogOpen(false)}
                onSubmit={data.submit}
                saving={data.saving}
                editing={data.editing}
                form={data.form}
                setForm={data.setForm}
                references={data.references}
                onSalvarPortal={data.salvarPortal}
                salvandoPortal={data.savingPortal}
            />

            <Dialog open={Boolean(data.confirmDelete)} onClose={() => data.setConfirmDelete(null)}>
                <DialogTitle>Excluir usuário</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja excluir o usuário <strong>{data.confirmDelete?.username || '-'}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => data.setConfirmDelete(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={data.confirmDeleteTarget}>Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
